import { useMemo, useState } from "react";
import {
  ColumnDef,
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  useReactTable,
} from "@tanstack/react-table";
import { useRecoilState } from "recoil";

import { TableTop, TableContainer, TableContents } from "style/admin/table";
import PaginationControls from "components/admin/table.components/pagination";
import AdminCommonModalLayout from "components/admin/common.modal.layout/admin.common.modal";
import { CalibrationDeleteModal } from "components/admin/contents/calibration/modal/calibration.delete.modal";
import { CalibrationReadDto } from "interface/admin/interface.calibration";
import { calibrationDeleteModalState } from "atoms/admin/contents/calibration.state"

interface AdminCalibrationTableProps {
  data: CalibrationReadDto[];
}

export function AdminCalibrationTable(props: AdminCalibrationTableProps) {
  const { data } = props;
  const [selected, setSelected] = useState<{ id: number, no: number, serial: string }>({ id: 0, no: 0, serial: "" });
  const [calibrationDeleteModal, setCalibrationDeleteModal] = useRecoilState(calibrationDeleteModalState);
  const handleDeleteModalClose = () => {
    setCalibrationDeleteModal({ isOpen: false, isVisible: false });
  }
  const onClickDelete = (id: number, no: number, serial: string) => {
    setSelected({ id, no, serial });
    setCalibrationDeleteModal({ isOpen: true, isVisible: true });
  };

  const columns = useMemo<ColumnDef<any>[]>(
    () => [
      {
        header: "Index",
        id: "number", // 넘버링을 위한 id
        cell: ({ row }) => row.index + 1, // 각 행의 인덱스에 1을 더해 넘버링
        size: 96,
      },
      {
        header: "시리얼번호",
        accessorKey: "serial",
        cell: (info) => info.getValue(),
        size: 200,
      },
      {
        header: "메모",
        accessorKey: "memo",
        cell: (info) => info.getValue(),
        size: 300,
      },
      {
        header: "등록일시",
        accessorKey: "createdAt",
        cell: (info) => info.getValue(),
        size: 160,
      },
      {
        header: "수정",
        id: "actions",
        cell: ({ row }) => {
          return (
            <div className="action-buttons">
              <button
                className="delete-btn"
                onClick={() => {
                  onClickDelete(row.original.id, row.index + 1, row.original.serial);
                }}
              >
                삭제
              </button>
            </div>
          );
        },
        size: 92,
      },
    ],
    []
  );
  const table = useReactTable({
    data: data,
    columns,
    enableRowSelection: true,
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    initialState: { pagination: { pageSize: 50 } }, // ✅ 초기 페이지 크기 설정
  });
  const paginationState = table.getState().pagination;
  const startRow = paginationState.pageIndex * paginationState.pageSize + 1;
  const endRow = startRow + table.getRowModel().rows.length - 1;
  const totalRows = data.length;
  return (
    <TableContainer>
      <TableTop>
        <span className="table-count">
          {totalRows} 중 {startRow}-{endRow}
        </span>
        <PaginationControls table={table} />
      </TableTop>
      <TableContents>
        <table
          className={
            table.getRowModel().rows.length === 0 ? "no-data-table" : ""
          }
        >
          <thead>
            {table.getHeaderGroups().map((item) => (
              <tr key={item.id}>
                {item.headers.map((header) => (
                  <th
                    key={header.id}
                    style={{ minWidth: `${header.getSize()}px` }}
                  >
                    {header.isPlaceholder
                      ? null
                      : flexRender(
                          header.column.columnDef.header,
                          header.getContext()
                        )}
                  </th>
                ))}
              </tr>
            ))}
          </thead>
          <tbody>
            {table.getRowModel().rows.length > 0 ? (
              table.getRowModel().rows.map((row) => (
                <tr
                  key={row.id}
                  onDoubleClick={() => {}} // ✅ 더블 클릭 시 상세 페이지 이동
                  style={{ cursor: "pointer" }} // ✅ 마우스 오버 시 클릭 가능하도록 스타일 추가
                >
                  {row.getVisibleCells().map((cell) => {
                    const isHideYn = cell.column.id === "useYn";
                    const isTitle = cell.column.id === "title";
                    const isProfile = cell.column.id === "userName";

                    return (
                      <td
                        key={cell.id}
                        className={`${isHideYn ? "hide-state" : ""} ${
                          isTitle ? "notice-title-cell" : ""
                        } ${isProfile ? "profile-cell" : ""}`.trim()} // ✅ 여러 개의 클래스 추가
                      >
                        {flexRender(
                          cell.column.columnDef.cell,
                          cell.getContext()
                        )}
                      </td>
                    );
                  })}
                </tr>
              ))
            ) : (
              // ✅ 데이터가 없을 경우 "검색 결과가 없습니다" 표시
              <tr className="no-data">
                <td colSpan={columns.length} className="no-data-td">
                  검색 결과가 없습니다.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </TableContents>

      {calibrationDeleteModal && (
        <AdminCommonModalLayout
          isVisible={calibrationDeleteModal.isVisible}
          closedHandler={handleDeleteModalClose}
        >
          <CalibrationDeleteModal
            closedHandler={handleDeleteModalClose}
            calibrationId={selected.id}
            calibrationSerial={selected.serial}
            orderNo={selected.no}
          />
        </AdminCommonModalLayout>
      )}
    </TableContainer>
  )
}
